///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The conversion ladder's final rung: after a generous engagement
/// threshold (see signinWallActive in engagementSlice), anonymous use ends
/// and the globe is blocked behind a required sign-in. One click via OAuth
/// creates the account. Deep-link sessions are exempt so shared links never
/// dead-end. Renders at z-40, below the auth dialog (z-50), so the dialog
/// opened by the CTA stays fully interactive above the wall.
///

import { Button } from '@/components/ui/button'
import { getBetaStatus } from '@/hooks/useBetaStatus'
import { setAuthOpen } from '@/redux/dialogSlice'
import { signinWallActive } from '@/redux/engagementSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useGetUserProfileQuery } from '@/redux/userApi'
import { track } from '@/utils/analytics'
import { Globe2 } from 'lucide-react'
import { useEffect, useRef } from 'react'

export function SignInWall() {
	const dispatch = useAppDispatch()
	const { data: userProfile, isLoading } = useGetUserProfileQuery()
	const { isBeta } = getBetaStatus()
	const engagement = useAppSelector(state => state.engagement)

	const visible = !isBeta && !isLoading && userProfile === undefined &&
		signinWallActive(engagement)

	// Funnel event: emitted once per appearance, not per re-render
	const capturedShown = useRef(false)
	useEffect(() => {
		if (!visible || capturedShown.current) return
		capturedShown.current = true
		track('signin_wall_shown', { session_count: engagement.sessionCount })
	}, [visible, engagement.sessionCount])

	if (!visible) return null

	const handleContinue = () => {
		track('signin_wall_cta_clicked', {})
		dispatch(setAuthOpen(true))
	}

	return (
		<div
			className="absolute inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-md animate-in fade-in duration-500"
			role="dialog"
			aria-modal="true"
			aria-label="Sign in required"
		>
			<div className="mx-4 max-w-md rounded-2xl border border-white/20 bg-black/40 backdrop-blur-xl p-8 text-center shadow-2xl">
				<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10">
					<Globe2 className="h-6 w-6 text-cyan-400" />
				</div>
				<h2 className="text-xl font-semibold text-white">
					Create a free account to continue
				</h2>
				<p className="mt-3 text-sm leading-relaxed text-white/70">
					You&apos;ve been exploring EarthBrowser for a while — glad to have
					you. A free account takes one click with Google, Apple, or
					Microsoft, and keeps your saved views on every device.
				</p>
				<Button
					onClick={handleContinue}
					className="mt-6 w-full rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
				>
					Sign in or create free account
				</Button>
				<p className="mt-3 text-xs text-white/50">
					EarthBrowser stays free — no card, no commitment
				</p>
			</div>
		</div>
	)
}
